import type { ReactElement } from 'react';
import { Text } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import { formatCount, formatPercent, formatTokens, formatUsd, humanUser } from '@/shared/analytics/MetricFormatting';
import { scoreColor } from '@/design/ScoreColors';
import type { DriftUserRow } from './DriftMetrics';

interface DriftUserGridProps {
    rows: DriftUserRow[];
    /** Builds the drill-in route for a person row (the L2 session → topic grid). */
    hrefFor: (userId: string) => string;
    persistenceKey?: string;
}

const COLUMNS: MegaGridColumn<DriftUserRow>[] = [
    {
        key: 'user',
        header: 'User',
        minWidth: 200,
        sortValue: (r) => humanUser(r.userId),
        render: (r) => (
            <Text size="xs" fw={600}>
                {humanUser(r.userId)}
            </Text>
        ),
    },
    {
        key: 'totalTopics',
        header: 'Total topics',
        align: 'right',
        sortValue: (r) => r.totalTopics,
        render: (r) => (
            <Text size="xs" ff="monospace">
                {formatCount(r.totalTopics)}
            </Text>
        ),
    },
    {
        key: 'totalSessions',
        header: 'Total sessions',
        align: 'right',
        sortValue: (r) => r.totalSessions,
        render: (r) => (
            <Text size="xs" ff="monospace">
                {formatCount(r.totalSessions)}
            </Text>
        ),
    },
    {
        key: 'topicsPerSession',
        header: 'Topics / session',
        align: 'right',
        sortValue: (r) => r.topicsPerSession,
        render: (r) => (
            <Text size="xs" ff="monospace">
                {r.topicsPerSession.toFixed(1)}
            </Text>
        ),
    },
    {
        key: 'totalTokens',
        header: 'Total tokens',
        align: 'right',
        sortValue: (r) => r.totalTokens,
        render: (r) => (
            <Text size="xs" ff="monospace">
                {formatTokens(r.totalTokens)}
            </Text>
        ),
    },
    {
        key: 'driftTokens',
        header: 'Drift tokens',
        align: 'right',
        sortValue: (r) => r.driftTokens,
        render: (r) => (
            <Text size="xs" ff="monospace">
                {formatTokens(r.driftTokens)}
            </Text>
        ),
    },
    {
        key: 'driftCost',
        header: 'Drift cost',
        align: 'right',
        noFilter: true,
        noGroup: true,
        sortValue: (r) => r.driftCostUsd,
        render: (r) => (
            <Text size="xs" ff="monospace">
                {formatUsd(r.driftCostUsd)}
            </Text>
        ),
    },
    {
        key: 'efficiency',
        header: 'Efficiency',
        align: 'right',
        sortValue: (r) => r.efficiency,
        render: (r) => (
            <Text size="xs" ff="monospace" fw={600} style={{ color: scoreColor(r.efficiency * 100) }}>
                {formatPercent(r.efficiency)}
            </Text>
        ),
    },
];

/** The people-first Session Drift overview — one row per person with drift, defaulting to topics-per-session descending. Clicking a row drills into that person's sessions. */
export default function DriftUserGrid({ rows, hrefFor, persistenceKey }: DriftUserGridProps): ReactElement {
    const navigate = useNavigate();
    const sorted = [...rows].sort((a, b) => b.topicsPerSession - a.topicsPerSession);

    return (
        <MegaGrid
            columns={COLUMNS}
            rows={sorted}
            getRowKey={(r) => r.userId}
            onRowClick={(r) => navigate(hrefFor(r.userId))}
            searchAccessor={(r) => humanUser(r.userId)}
            searchPlaceholder="Search people"
            striped
            density="comfortable"
            clientPageSize={25}
            persistenceKey={persistenceKey}
            enableExport
            exportName="session-drift-people"
            minWidth={760}
        />
    );
}
